import {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type TableDensity = "compact" | "comfortable";

export type PersistedTableState = {
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  globalFilter?: string;
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  columnSizing?: ColumnSizingState;
  columnPinning?: ColumnPinningState;
  density?: TableDensity;
};

function storageKey(key: string) {
  return `dataTable:${key}`;
}

function urlParamName(key: string) {
  return `dt.${key}`;
}

function readLocalStorage(key: string | undefined): PersistedTableState {
  if (!key) return {};
  try {
    const raw = localStorage.getItem(storageKey(key));
    return raw ? (JSON.parse(raw) as PersistedTableState) : {};
  } catch {
    return {};
  }
}

function writeLocalStorage(
  key: string | undefined,
  state: PersistedTableState,
) {
  if (!key) return;
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded) - view state just won't persist.
  }
}

function readUrlState(
  searchParams: URLSearchParams,
  key: string | undefined,
): PersistedTableState {
  if (!key) return {};
  const raw = searchParams.get(urlParamName(key));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as PersistedTableState;
  } catch {
    return {};
  }
}

// Loads a DataTable's view state (sort/filters/visibility/order/sizing/pinning/density), preferring
// the URL (so a shared link reproduces the sender's view) and falling back to localStorage, keyed by
// `persistStateKey`. Every subsequent change is written back to both (debounced by the caller).
export function useTableViewState(
  persistStateKey: string | undefined,
  enableUrlSync: boolean | undefined,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKey = enableUrlSync ? persistStateKey : undefined;

  const [initial] = useState<PersistedTableState>(() => {
    const fromUrl = readUrlState(searchParams, urlKey);
    return Object.keys(fromUrl).length > 0
      ? fromUrl
      : readLocalStorage(persistStateKey);
  });

  // Avoids re-reading `searchParams` reactively (which would fight with our own updates below);
  // we only ever read the URL once, at mount, via the lazy `initial` state above.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const persist = useCallback(
    (state: PersistedTableState) => {
      writeLocalStorage(persistStateKey, state);
      if (urlKey) {
        setSearchParamsRef.current(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(urlParamName(urlKey), JSON.stringify(state));
            return next;
          },
          { replace: true },
        );
      }
    },
    [persistStateKey, urlKey],
  );

  return { initial, persist };
}
